import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  Grid,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useToken } from '../../../hooks/useToken';

/**
 * Modal para editar una columna existente en un tablero Kanban
 * @param {boolean} open - Estado que controla si el modal está abierto
 * @param {Function} onClose - Función para cerrar el modal
 * @param {Function} onSave - Función para guardar los cambios de la columna
 * @param {Object} column - Datos de la columna a editar
 * @returns {JSX.Element} Componente de modal
 */
const EditColumnModal = ({ open, onClose, onSave, column }) => {
  const { getTokenSilent } = useToken();

  // Estados para el control del modal
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados para los campos del formulario
  const [columnName, setColumnName] = useState('');
  const [columnDescription, setColumnDescription] = useState('');
  const [nameError, setNameError] = useState('');

  // Estado para manejar metadatos adicionales
  const [metaDataFields, setMetaDataFields] = useState([]);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [showAddMetaField, setShowAddMetaField] = useState(false);

  // Determinar el tipo de tablero basado en la URL
  const isSupportBoard = window.location.pathname.includes('/soporte');

  /**
   * Resetea el formulario a su estado inicial
   */
  const resetForm = useCallback(() => {
    setNameError('');
    setError(null);

    // Si hay una columna, cargar sus datos
    if (column) {
      setColumnName(column.name || '');

      // Cargar metadatos si existen
      if (column.meta_data) {
        try {
          let metaData = {};

          // Convertir meta_data a objeto si es string
          if (typeof column.meta_data === 'string') {
            metaData = JSON.parse(column.meta_data);
          } else if (typeof column.meta_data === 'object') {
            metaData = column.meta_data;
          }

              // Crear copia para evitar mutar el objeto original
    const metaDataCopy = { ...metaData };
    
    // Extraer la descripción si existe
    if (metaDataCopy.description) {
      setColumnDescription(metaDataCopy.description);
      // Eliminar la descripción de la COPIA para no mostrarla como un campo separado
      delete metaDataCopy.description;
    } else {
      setColumnDescription('');
    }

          // Convertir el objeto de metadatos a array de campos usando la COPIA
          const fields = Object.entries(metaDataCopy).map(([key, value]) => ({
            key,
            value: String(value)
          }));

          setMetaDataFields(fields);
        } catch (error) {
          console.error('Error al parsear metadatos:', error);
          setMetaDataFields([]);
          setColumnDescription('');
        }
      } else {
        setMetaDataFields([]);
        setColumnDescription('');
      }
    } else {
      setColumnName('');
      setMetaDataFields([]);
      setColumnDescription('');
    }

    setNewKey('');
    setNewValue('');
    setShowAddMetaField(false);
  }, [column]);

  // Cargar datos de la columna cuando se abre el modal
  useEffect(() => {
    if (open && column) {
      resetForm();
    }
  }, [open, column, resetForm]);

  /**
   * Maneja cambios en el campo de nombre de columna
   * @param {Object} e - Evento de cambio
   */
  const handleNameChange = (e) => {
    setColumnName(e.target.value);
    if (e.target.value.trim()) {
      setNameError('');
    }
  };

  /**
   * Maneja cambios en el campo de descripción
   * @param {Object} e - Evento de cambio
   */
  const handleDescriptionChange = (e) => {
    setColumnDescription(e.target.value);
  };

  /**
   * Añade un nuevo campo de metadatos al formulario
   */
  const handleAddMetaField = () => {
    if (newKey.trim() && newValue.trim()) {
      setMetaDataFields([
        ...metaDataFields,
        { key: newKey.trim(), value: newValue.trim() }
      ]);
      setNewKey('');
      setNewValue('');
      setShowAddMetaField(false);
    }
  };

  /**
   * Elimina un campo de metadatos del formulario
   * @param {number} index - Índice del campo a eliminar
   */
  const handleRemoveMetaField = (index) => {
    // Crear una copia del array para no modificar el original directamente
    const updatedFields = metaDataFields.filter((_, idx) => idx !== index);

    // Actualizar el estado con el nuevo array (sin el elemento eliminado)
    setMetaDataFields(updatedFields);
  };

  /**
   * Construye el objeto de metadatos en formato JSON
   * @returns {string|null} String JSON de metadatos o null si no hay datos
   */
  const buildMetaData = () => {
    // Crear un objeto vacío para los metadatos
    const metaData = {};

    // Añadir todos los campos de metadatos del formulario
    // Solo se incluirán los que no han sido eliminados
    metaDataFields.forEach(field => {
      if (field.key && field.key.trim()) { // Verificar que la clave no esté vacía
        metaData[field.key] = field.value;
      }
    });

    // Añadir la descripción si existe
    if (columnDescription.trim()) {
      metaData.description = columnDescription.trim();
    }

    // Determinar el tipo de tablero basado en la URL o el contexto
    if (isSupportBoard) {
      // Si estamos en un tablero de soporte, añadir el tipo
      metaData.type = 'soporte';
    }
    // No añadimos type: 'general' para tableros generales

    return Object.keys(metaData).length > 0 ? JSON.stringify(metaData) : null;
  };

  /**
   * Capitaliza la primera letra de un string
   * @param {string} str - String a capitalizar
   * @returns {string} String con la primera letra en mayúscula
   */
  const capitalizeFirstLetter = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  /**
   * Valida el formulario antes de enviar
   * @returns {boolean} True si el formulario es válido, false en caso contrario
   */
  const validateForm = () => {
    let isValid = true;

    // Validar nombre de columna
    if (!columnName.trim()) {
      setNameError('El nombre de la columna es obligatorio');
      isValid = false;
    }

    return isValid;
  };

  /**
   * Maneja el envío del formulario
   */
  const handleSubmit = async () => {
    // Validar formulario
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getTokenSilent();

      if (!token) {
        throw new Error('No se pudo obtener el token de autenticación');
      }

      if (!column || !column.id) {
        throw new Error('ID de la columna no especificado');
      }

      // Preparar los datos de la columna
      const columnData = {
        // Capitalizar primera letra del nombre
        name: capitalizeFirstLetter(columnName.trim()),
        token
      };

      // Construir los metadatos
      const metaDataJson = buildMetaData();

      // Siempre enviar meta_data, incluso si es un objeto vacío
      // Esto asegura que los metadatos eliminados se eliminen realmente
      columnData.meta_data = metaDataJson || '{}';

      // Guardar los cambios
      await onSave(column.id, columnData);

      // Cerrar el modal
      onClose();
    } catch (error) {
      console.error('Error al actualizar columna:', error);
      setError(error.message || 'Error al actualizar la columna');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Maneja el cierre del modal
   */
  const handleClose = () => {
    onClose();
  };

  /**
   * Renderiza la sección de metadatos
   * @returns {JSX.Element} Sección de metadatos
   */
  const renderMetadataSection = () => (
    <Box sx={{ mt: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1">Información adicional</Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={() => setShowAddMetaField(true)}
          disabled={loading}
          variant="outlined"
          size="small"
        >
          Añadir campo
        </Button>
      </Box>

      {/* Formulario para añadir metadatos */}
      {showAddMetaField && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={5}>
            <TextField
              size="small"
              label="Nombre del campo"
              fullWidth
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              size="small"
              label="Valor"
              fullWidth
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={2}>
            <Box display="flex" alignItems="center" height="100%">
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddMetaField}
                disabled={loading || !newKey.trim() || !newValue.trim()}
                size="small"
              >
                Añadir
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Lista de metadatos */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {metaDataFields.map((field, index) => (
          <Chip
            key={index}
            label={`${field.key}: ${field.value}`}
            onDelete={() => handleRemoveMetaField(index)}
            disabled={loading}
            deleteIcon={<DeleteIcon />}
          />
        ))}
        {metaDataFields.length === 0 && !showAddMetaField && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No hay campos adicionales. Haga clic en "Añadir campo" para agregar información personalizada.
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="edit-column-dialog-title"
    >
      <DialogTitle id="edit-column-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Editar Columna</Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ pt: 1 }}>
          {/* Nombre de la columna */}
          <TextField
            autoFocus
            margin="dense"
            label="Nombre de la columna *"
            type="text"
            fullWidth
            variant="outlined"
            value={columnName}
            onChange={handleNameChange}
            disabled={loading}
            error={!!nameError}
            helperText={nameError}
            sx={{ mb: 2 }}
          />

          {/* Descripción de la columna */}
          <TextField
            margin="dense"
            label="Descripción de la columna"
            type="text"
            fullWidth
            variant="outlined"
            value={columnDescription}
            onChange={handleDescriptionChange}
            disabled={loading}
            multiline
            rows={2}
            placeholder="Añade una descripción para esta columna (opcional)"
            sx={{ mb: 2 }}
          />

          {/* Sección de metadatos */}
          {renderMetadataSection()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditColumnModal;
